import React, { useCallback, useMemo, useState } from 'react';
import { ApolloError, useMutation, useQuery } from '@apollo/client';
import { Alert, Button, Modal, Space, Table, Tag, Typography } from 'antd';
import { ColumnsType } from 'antd/es/table';
// Api
import { recruitmentClient } from 'api/Recruitment/client';
import { IMPORT_CSV, GET_CSV_UPLOAD_URL } from 'api/Recruitment/mutations';
import { GET_CSV_EXAMPLE, GET_CSV_SCHEMA } from 'api/Recruitment/queries';
import { parseRecruitmentApiError } from 'api/Recruitment/errors';
// Types
import {
  GetCsvSchema,
  GetCsvSchema_getCsvSchema_columns,
} from 'api/Recruitment/types/GetCsvSchema';
import { GetCsvExample } from 'api/Recruitment/types/GetCsvExample';
import {
  GetCsvUploadUrl,
  GetCsvUploadUrlVariables,
} from 'api/Recruitment/types/GetCsvUploadUrl';
import { ImportCsv, ImportCsvVariables } from 'api/Recruitment/types/ImportCsv';
// UI
import { errorNotification, successNotification } from 'ui/Notification';
// Styles
import styles from './ImportCsvModal.module.scss';

const { Text } = Typography;

type ImportCsvModalProps = {
  isVisible: boolean;
  onClose: () => void;
  onSuccess: () => Promise<void> | void;
};

const getMissingColumns = (errorInfo?: {
  missingColumns?: string[];
  missingRequiredColumns?: string[];
  requiredColumns?: string[];
}): string[] => {
  return (
    errorInfo?.missingColumns ||
    errorInfo?.missingRequiredColumns ||
    errorInfo?.requiredColumns ||
    []
  );
};

const getErrorMessage = (error: unknown): string | undefined => {
  if (error instanceof ApolloError) {
    const parsedError = parseRecruitmentApiError(error);

    if (parsedError.errorType === 'CsvHeaderInvalidError') {
      const missingColumns = getMissingColumns(parsedError.errorInfo);

      return missingColumns.length
        ? `Your CSV is missing required columns: ${missingColumns.join(', ')}.`
        : 'Your CSV header is invalid. Please download the example CSV and make sure the required columns are present.';
    }

    if (parsedError.errorType === 'UnauthorizedException') {
      return 'Session expired, please log in again.';
    }

    if (parsedError.errorType === 'InternalError') {
      return 'CSV import failed because of a server error. Please try again.';
    }
  }

  return undefined;
};

const ImportCsvModal = ({
  isVisible,
  onClose,
  onSuccess,
}: ImportCsvModalProps): JSX.Element => {
  const [csvFile, setCsvFile] = useState<File | null>(null);
  const [inlineError, setInlineError] = useState<string>('');
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
  const {
    data: schemaData,
    error: schemaError,
    loading: loadingSchema,
  } = useQuery<GetCsvSchema>(GET_CSV_SCHEMA, {
    client: recruitmentClient,
    skip: !isVisible,
  });

  const [getCsvUploadUrl] = useMutation<
    GetCsvUploadUrl,
    GetCsvUploadUrlVariables
  >(GET_CSV_UPLOAD_URL, {
    client: recruitmentClient,
  });
  const [importCsv] = useMutation<ImportCsv, ImportCsvVariables>(IMPORT_CSV, {
    client: recruitmentClient,
  });

  const columns = useMemo<ColumnsType<GetCsvSchema_getCsvSchema_columns>>(
    () => [
      {
        title: 'Column',
        dataIndex: 'name',
        key: 'name',
      },
      {
        title: 'Label',
        dataIndex: 'label',
        key: 'label',
      },
      {
        title: 'Required',
        dataIndex: 'required',
        key: 'required',
        render: (required: boolean | null) =>
          required ? (
            <Tag color="green">Required</Tag>
          ) : (
            <Tag color="default">Optional</Tag>
          ),
      },
      {
        title: 'Description',
        dataIndex: 'description',
        key: 'description',
      },
      {
        title: 'Examples',
        dataIndex: 'examples',
        key: 'examples',
        render: (examples: string[] | null) => examples?.join(', ') || '-',
      },
    ],
    []
  );

  const handleFileChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      setInlineError('');
      setCsvFile(event.target.files?.[0] || null);
    },
    []
  );

  const handleDownloadExample = async () => {
    try {
      const result = await recruitmentClient.query<GetCsvExample>({
        fetchPolicy: 'network-only',
        query: GET_CSV_EXAMPLE,
      });
      const csvExampleUrl = result.data?.getCsvExample;

      if (!csvExampleUrl) {
        errorNotification('CSV example could not be downloaded.');
        return;
      }

      window.open(csvExampleUrl, '_blank', 'noopener,noreferrer');
    } catch (error) {
      errorNotification('CSV example could not be downloaded.');
    }
  };

  const uploadCsvToS3 = async (url: string, fields: string, file: File) => {
    const form = new FormData();
    const parsedFields = JSON.parse(fields) as Record<string, string>;

    Object.entries(parsedFields).forEach(([key, value]) => {
      form.append(key, value);
    });
    form.append('Content-Type', 'text/csv');
    form.append('file', file);

    const response = await fetch(url, { method: 'POST', body: form });

    if (response.status !== 204) {
      errorNotification('Upload to storage failed');
      throw new Error('Upload to storage failed');
    }
  };

  const handleSubmit = async () => {
    if (!csvFile) {
      setInlineError('Choose a CSV file before submitting.');
      return;
    }

    try {
      setInlineError('');
      setIsSubmitting(true);
      const uploadResult = await getCsvUploadUrl({
        variables: { filename: csvFile.name },
      });
      const upload = uploadResult.data?.getCsvUploadUrl;

      if (!upload) {
        errorNotification('Upload URL could not be created.');
        return;
      }

      await uploadCsvToS3(upload.url, upload.fields, csvFile);
      const importResult = await importCsv({ variables: { key: upload.key } });
      const jobId = importResult.data?.importCsv?.jobId;

      successNotification(
        jobId
          ? `Import job ${jobId} has been submitted.`
          : 'Import job has been submitted.'
      );
      setCsvFile(null);
      await onSuccess();
    } catch (error) {
      const message = getErrorMessage(error);

      if (message) {
        if (message === 'Session expired, please log in again.') {
          errorNotification(message);
        } else if (message.includes('server error')) {
          errorNotification(message);
        } else {
          errorNotification(message, 'CSV validation failed');
          setInlineError(message);
        }
      } else if ((error as Error).message === 'Upload to storage failed') {
        errorNotification('Upload to storage failed.');
      } else {
        errorNotification('CSV import could not be submitted.');
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Modal
      destroyOnClose
      okButtonProps={{ disabled: !csvFile, loading: isSubmitting }}
      okText="Submit"
      onCancel={isSubmitting ? undefined : onClose}
      onOk={handleSubmit}
      title="Import External Talents CSV"
      visible={isVisible}
      width={900}
    >
      <Space direction="vertical" size="middle" className={styles.content}>
        <Space align="center">
          <input
            accept=".csv,text/csv"
            onChange={handleFileChange}
            type="file"
          />
          {csvFile && <Text>{csvFile.name}</Text>}
        </Space>

        <Button onClick={handleDownloadExample}>Download CSV Example</Button>

        {inlineError && <Alert message={inlineError} type="error" showIcon />}

        {schemaError && (
          <Alert
            message="CSV column requirements could not be loaded."
            type="error"
            showIcon
          />
        )}

        {schemaData?.getCsvSchema?.description && (
          <Text type="secondary">{schemaData.getCsvSchema.description}</Text>
        )}

        <div>
          <Text strong>CSV Columns</Text>
          <Table<GetCsvSchema_getCsvSchema_columns>
            columns={columns}
            dataSource={schemaData?.getCsvSchema?.columns || []}
            loading={loadingSchema}
            pagination={false}
            rowKey="name"
            size="small"
            className={styles.schemaTable}
          />
        </div>
      </Space>
    </Modal>
  );
};

export default ImportCsvModal;
